import { Link } from 'react-router-dom';
import { PublicFooter, PublicHeader } from './PublicPage.js';

const STATS: [string, string][] = [
  ['9', 'Constraints checked before a slot is offered'],
  ['4', 'Minutes from sign-up to a first reservation'],
  ['$15', 'Flat monthly for a freelance CFI'],
  ['1', 'Places to check before saying yes to a lesson'],
];

/* The mark colour carries the meaning here, so each feature takes the system
   hue its subject uses elsewhere in the app. */
const FEATURES: [string, string, string][] = [
  ['One grid for the whole ramp',
    'Aircraft and instructors as rows, hours across. Open a reservation and the student, the instructor and the invoice are all on it.',
    'var(--sys-blue)'],
  ['Checked before they exist',
    'Maintenance status, duty policy, endorsement dates and syllabus order are evaluated when the slot is offered, not when the flight is booked.',
    'var(--sys-green)'],
  ['Grounding is one action',
    'File a squawk and every affected reservation surfaces immediately, with a message ready for each student.',
    'var(--sys-red)'],
  ['Students book themselves',
    'The portal offers only times that already pass the checks, and shows how many openings each day holds before anyone picks one.',
    'var(--sys-purple)'],
  ['Duty and currency in view',
    'Instruction hours against your school policy, certificates and endorsements against the calendar. Stated on the schedule rather than remembered.',
    'var(--sys-orange)'],
  ['A day view for the ramp',
    "Today's flights, the tail number and the tie-down, and two actions sized for a gloved thumb. Caches at 04:00 and queues entries until signal returns.",
    'var(--sys-gray)'],
];

const CHECKS: [string, string, string, string][] = [
  ['Aircraft', 'Whether the airframe is booked, grounded, or inside another reservation', 'Fleet record', 'var(--sys-blue)'],
  ['Airworthiness', 'Open squawks and their operational effect, from day-VFR-only to grounded', '14 CFR 91.7', 'var(--sys-red)'],
  ['Inspection', 'Hours remaining to the 100-hour or annual, projected at current utilization', '14 CFR 91.409', 'var(--sys-red)'],
  ['Instructor duty', 'Instruction and duty hours accumulated against the policy you set', 'School policy', 'var(--sys-orange)'],
  ['Instructor certificate', 'CFI, CFII and MEI expiry, and the renewal window', '14 CFR 61.197', 'var(--sys-orange)'],
  ['Student endorsements', 'Solo and cross-country solo endorsements and their expiry dates', '14 CFR 61.87, 61.93', 'var(--sys-orange)'],
  ['Medical and documents', 'Class and expiry of the medical, written results, TSA citizenship check', '14 CFR 61.23', 'var(--sys-gray)'],
  ['Syllabus position', 'Stage checks and lesson order, gating the next lesson under Part 141', '14 CFR 141 App. B', 'var(--sys-purple)'],
  ['Daylight', 'Whether a day lesson still fits before official sunset at the field', '14 CFR 1.1', 'var(--sys-gray)'],
];

const AUDIENCES: [string, string, string, string, string[]][] = [
  ['who-freelance', 'Freelance CFI', 'One instructor, one hat', 'var(--sys-blue)', [
    'One rate card, one calendar, one invoice trail.',
    'No dispatch desk to staff and no approvals to route.',
    'Rental aircraft pass through to the owner or the FBO.',
    'Fifteen dollars a month, one aircraft and one instructor.',
  ]],
  ['who-part61', 'Part 61 school', 'Instructors own the training', 'var(--sys-green)', [
    'Lesson order stays advisory; instructors reorder without approval.',
    'Dispatch owns aircraft assignment and check-out.',
    'Instructors see their own hours and earnings, not the school ledger.',
    'Solo endorsements by the instructor of record.',
  ]],
  ['who-part141', 'Part 141 school', 'Records that survive an audit', 'var(--sys-purple)', [
    'Stage checks gate the next lesson, visibly and with the citation.',
    'Chief instructor approves stage records and course completions.',
    'Dispatch can move a reservation but never a syllabus position.',
    'Enrollment agreements and stage-based billing supported.',
  ]],
];

const ROLES: [string, string, string, string][] = [
  ['Management', 'Fleet, instructors, training records, rates and invoices.', 'Full access', 'var(--sys-blue)'],
  ['Instructor', 'Own reservations, own students, own earnings. Squawk filing included.', 'Scoped', 'var(--sys-green)'],
  ['Dispatch', 'Aircraft, reservations and airworthiness. Training grades hidden.', 'Scoped', 'var(--sys-orange)'],
  ['Student', 'Own lessons, requests and account balance.', 'Own record', 'var(--sys-purple)'],
];

const PLANS: [string, string, string, string, string[], string][] = [
  ['Freelance', 'One CFI, one aircraft', '$15', 'per month', [
    'Resource grid and ramp day view',
    'Student portal and requests',
    'All nine checks',
    'Hobbs to invoice',
    'Email support',
  ], 'Start free trial'],
  ['School', 'Part 61 flight schools', "Let's talk", 'priced to your fleet', [
    'Unlimited instructors and students',
    'Dispatch role and check-out',
    'Maintenance and squawk tracking',
    'School rate card and invoicing',
    'Duty policy you define',
  ], 'Contact sales'],
  ['Program', 'Part 141 certificated programs', "Let's talk", 'priced to your fleet', [
    'Everything in School',
    'Syllabus gating and stage checks',
    'Chief instructor approvals',
    'Training record and stage export',
    'Enrollment agreements',
  ], 'Contact sales'],
];

const FAQS: [string, string][] = [
  ['Does FlightSlots decide whether a flight is legal?',
    'No. It reports findings with the fact and the rule each one came from, and the pilot in command decides. No screen in the product says cleared or approved.'],
  ['Can I use it for a single rented airplane?',
    'Yes, and that is the freelance plan at fifteen dollars a month. One aircraft and one instructor, with every check included.'],
  ['What happens when an aircraft goes down?',
    'Filing a squawk grounds it for every future reservation and lists each affected booking with a message ready to send. One action, not a hunt through the calendar.'],
  ['Do you enforce instructor duty limits?',
    'We report against the policy your school sets, and we label it as school policy on every finding. Part 61 and 141 instruction carries no federal duty limit, and we will not imply one.'],
  ['How do students book?',
    'Through a portal that only offers times already passing the checks. A request holds the slot for twelve hours while the instructor confirms.'],
  ['Can I get my records out?',
    'Training records, stage records and invoices export in full, with the regulation applied to each finding. Your records are yours.'],
];

export function Landing(): JSX.Element {
  return (
    <div className="lp">
      <PublicHeader />

      <section className="lp__hero">
        <div className="lp__wrap">
          <div className="lp__eyebrow">FlightSlots</div>
          <h1 className="lp__h1">
            Scheduling that knows what your aircraft and your students are allowed to do.
          </h1>
          <p className="lp__lede">
            One calendar for the fleet, the instructors and the syllabus. Every reservation is
            checked against maintenance, duty policy, currency and 141 stage order before it exists.
          </p>
          <div className="lp__actions">
            <Link to="/signup" className="btn btn--primary btn--lg" style={{ textDecoration: 'none' }}>
              Start your free trial
            </Link>
            <Link to="/login" className="btn btn--tinted btn--lg" style={{ textDecoration: 'none' }}>
              See a live schedule
            </Link>
          </div>
          <div className="lp__fine">$15 a month for a freelance CFI. Schools and 141 programs, talk to us.</div>
        </div>
        {/* Both renders ship and CSS shows the one matching the theme, so the
            toggle swaps them without a fetch and without a flash of the wrong one. */}
        <div className="lp__shot">
          <img className="lp__shot--light" src="/hero-schedule-light.png"
            alt="The FlightSlots resource grid: aircraft and instructors as rows, hours across, with dual, solo, checkride, rental, requested, conflict and maintenance reservations." />
          <img className="lp__shot--dark" src="/hero-schedule-dark.png" alt="" aria-hidden="true" />
        </div>
      </section>

      <section className="lp__wrap lp__pad">
        <div className="lp__grid lp__grid--stats">
          {STATS.map(([value, label]) => (
            <div className="card lp__card" key={label}>
              <div className="lp__stat">{value}</div>
              <div className="lp__statk">{label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="lp__wrap lp__wrap--narrow lp__pad" style={{ textAlign: 'center' }}>
        <h2 className="lp__h2">
          A double-booked airplane is discovered on the ramp, and by then it has cost you the lesson.
        </h2>
        <p className="lp__sub">
          Most tools treat a flight lesson as a meeting. It is not. It needs an airworthy aircraft,
          an instructor inside a defensible duty window, a student whose endorsements are current,
          and under Part 141 a syllabus position that permits the lesson at all. Four constraints,
          held by four different people, checked together by none of them.
        </p>
      </section>

      <section id="how" className="lp__wrap" style={{ paddingTop: 44 }}>
        <div className="lp__grid">
          {FEATURES.map(([title, body, color]) => (
            <div className="card lp__card" key={title}>
              <span className="lp__mark" style={{ background: color }} />
              <div className="lp__cardt">{title}</div>
              <div className="lp__cardb">{body}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="checks" className="lp__wrap lp__pad">
        <div className="lp__head">
          <h2 className="lp__h2">Nine constraints, evaluated on every reservation</h2>
          <p className="lp__sub">
            Every finding carries the fact it was drawn from and the rule it was measured against.
            Nothing is hidden, and nothing is approved.
          </p>
        </div>
        <div className="card lp__list">
          {CHECKS.map(([name, body, reg, color]) => (
            <div className="lp__lrow" key={name}>
              <span className="lp__dot" style={{ background: color }} />
              <span className="lp__lk">{name}</span>
              <span className="lp__lv">{body}</span>
              <span className="lp__reg">{reg}</span>
            </div>
          ))}
        </div>
        <p className="note">
          Duty limits are the policy your school sets, not a federal limit for Part 61 or 141
          instruction. FlightSlots labels them as policy on every finding — the{' '}
          <Link to="/terms#advisory">advisory clause</Link> in our terms says the same thing.
        </p>
      </section>

      <section id="who" className="lp__wrap lp__pad">
        <div className="lp__head">
          <h2 className="lp__h2">Set up for how your operation is certificated</h2>
        </div>
        <div className="lp__grid">
          {AUDIENCES.map(([anchor, title, who, color, points]) => (
            <div className="card lp__card" id={anchor} key={title}>
              <span className="lp__mark" style={{ background: color }} />
              <div className="lp__cardt">{title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--label-3)' }}>{who}</div>
              <div className="lp__rule" />
              {points.map((point) => (
                <div className="lp__bullet" key={point}><span /><span>{point}</span></div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="lp__wrap lp__pad">
        <div className="card lp__card" style={{ padding: 28 }}>
          <div className="lp__grid lp__grid--wide">
            <div>
              <h2 className="lp__h3">Four roles, one schedule</h2>
              <p className="lp__sub" style={{ margin: '14px 0 0' }}>
                Permissions follow the organization rather than a settings page. A freelance CFI has
                no dispatch desk to grant, and a Part 61 school has no stage gates to enforce.
              </p>
            </div>
            <div className="lp__list">
              {ROLES.map(([name, body, tag, color]) => (
                <div key={name} style={{ display: 'flex', gap: 11, padding: '12px 0' }}>
                  <span className="lp__mark" style={{ background: color, width: 26, height: 26, marginTop: 2 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: 14.5, fontWeight: 600 }}>{name}</span>
                      <span className={`pill ${tag === 'Full access' ? 'pill--blue' : 'pill--gray'}`}>{tag}</span>
                    </span>
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--label-2)', marginTop: 1, textWrap: 'pretty' }}>
                      {body}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lp__wrap lp__wrap--tight lp__pad" style={{ textAlign: 'center' }}>
        <p className="lp__quote">
          &ldquo;Two airplanes and eleven students. The thing I stopped doing was checking three
          places before I said yes to a lesson.&rdquo;
        </p>
        <div className="lp__attrib">Independent CFI · CFII · 1,400 hrs given · Hagerstown, MD</div>
      </section>

      <section id="pricing" className="lp__wrap lp__pad">
        <div className="lp__head">
          <h2 className="lp__h2">Flat for one instructor, quoted for a school</h2>
          <p className="lp__sub">
            Flat monthly for a freelance instructor. Schools and 141 programs are quoted to the
            fleet, because no two are the same size.
          </p>
        </div>
        <div className="lp__grid" style={{ alignItems: 'start' }}>
          {PLANS.map(([name, forWho, price, unit, items, cta], i) => (
            <div className={`card lp__card${i === 0 ? ' lp__plan--on' : ''}`} key={name}>
              {i === 0 && <span className="pill pill--blue lp__flag">Self-serve</span>}
              <div className="lp__cardt" style={{ marginTop: 0 }}>{name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--label-3)' }}>{forWho}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 20 }}>
                <span className={`lp__price${i === 0 ? ' lp__price--lead' : ''}`}>{price}</span>
                <span className="lp__unit">{unit}</span>
              </div>
              <div className="lp__rule" />
              {items.map((item) => (
                <div className="lp__bullet" key={item}>
                  <span style={{ background: 'var(--sys-green)' }} /><span>{item}</span>
                </div>
              ))}
              <Link
                to={i === 0 ? '/signup' : `/contact?about=sales&plan=${i === 1 ? 'school' : 'program'}`}
                className={`btn btn--block ${i === 0 ? 'btn--primary' : 'btn--tinted'}`}
                style={{ marginTop: 18, textDecoration: 'none' }}
              >
                {cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="lp__wrap lp__pad">
        <div className="lp__head">
          <h2 className="lp__h2">Questions</h2>
        </div>
        <div className="card lp__list">
          {FAQS.map(([question, answer]) => (
            <div key={question} style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>{question}</div>
              <div style={{ fontSize: 13.5, color: 'var(--label-2)', marginTop: 5, maxWidth: '60em', textWrap: 'pretty' }}>
                {answer}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="lp__band">
        <div className="lp__bandk">Advisory, always</div>
        <p className="lp__bandt">
          FlightSlots never says cleared. It reports what needs review, with the facts and the
          regulation it used. The pilot in command decides.
        </p>
        <div className="lp__actions" style={{ marginTop: 30 }}>
          <Link to="/signup" className="btn btn--lg" style={{ textDecoration: 'none' }}>
            Start your free trial
          </Link>
          <Link to="/contact?about=sales&plan=program" className="btn btn--lg btn--ghost" style={{ textDecoration: 'none' }}>
            Talk to us about 141
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
